<template>
  <div class="relogio-circular" :class="{ 'relogio-smartphone': modoSmartphone }">
    <svg
      ref="svgRef"
      viewBox="0 0 300 300"
      class="relogio-svg"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
    >
      <!-- Anel externo -->
      <circle cx="150" cy="150" r="142" class="relogio-anel" />
      <!-- Disco central -->
      <circle cx="150" cy="150" r="104" class="relogio-disco" />

      <!-- Ponteiro -->
      <line
        x1="150"
        y1="150"
        :x2="ponteiroX"
        :y2="ponteiroY"
        class="relogio-ponteiro"
      />
      <circle cx="150" cy="150" r="6" class="relogio-eixo" />
      <circle :cx="ponteiroX" :cy="ponteiroY" r="17" class="relogio-ponta" />

      <!-- Rótulos -->
      <text
        v-for="item in rotulos"
        :key="item.valor"
        :x="item.x"
        :y="item.y"
        class="relogio-rotulo"
        :class="{ 'relogio-rotulo-ativo': item.valor === valorAtual }"
        text-anchor="middle"
        dominant-baseline="central"
      >{{ item.texto }}</text>
    </svg>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  // Valor selecionado (0-23 para hora, 0-59 para minuto/segundo)
  modelValue: { type: Number, default: 0 },
  // Tipo de seleção: 'hora' | 'minuto' | 'segundo'
  tipo: {
    type: String,
    default: 'hora',
    validator: (v) => ['hora', 'minuto', 'segundo'].includes(v)
  },
  // Modo smartphone: ajuste visual (o arrasto/swipe funciona em qualquer modo)
  modoSmartphone: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue', 'selecionar'])

const svgRef = ref(null)
const dragging = ref(false)
const valorAtual = ref(props.modelValue)

watch(() => props.modelValue, (v) => {
  valorAtual.value = v
})

const CX = 150
const CY = 150

// Raios dos rótulos
const RAIO_ROTULO_EXTERNO = 122
const RAIO_ROTULO_INTERNO = 80
// Raios do ponteiro
const RAIO_PONTEIRO_EXTERNO = 100
const RAIO_PONTEIRO_INTERNO = 62
// Limiar (distância do centro) que separa o anel interno do externo
const LIMIAR_ANEL = 90

const ehHora = computed(() => props.tipo === 'hora')

const posicaoAngulo = (index, total, raio) => {
  const rad = ((index / total) * 360 - 90) * (Math.PI / 180)
  return {
    x: CX + raio * Math.cos(rad),
    y: CY + raio * Math.sin(rad)
  }
}

const rotulos = computed(() => {
  const lista = []
  if (ehHora.value) {
    // 0-11 no anel externo, 12-23 no anel interno
    for (let i = 0; i < 12; i++) {
      const pOut = posicaoAngulo(i, 12, RAIO_ROTULO_EXTERNO)
      lista.push({ valor: i, x: pOut.x, y: pOut.y, texto: String(i).padStart(2, '0') })
      const pIn = posicaoAngulo(i, 12, RAIO_ROTULO_INTERNO)
      lista.push({ valor: i + 12, x: pIn.x, y: pIn.y, texto: String(i + 12).padStart(2, '0') })
    }
  } else {
    for (let v = 0; v < 60; v += 5) {
      const p = posicaoAngulo(v, 60, RAIO_ROTULO_EXTERNO)
      lista.push({ valor: v, x: p.x, y: p.y, texto: String(v).padStart(2, '0') })
    }
  }
  return lista
})

const raioPonteiroValor = computed(() => {
  if (ehHora.value) {
    return valorAtual.value >= 12 ? RAIO_PONTEIRO_INTERNO : RAIO_PONTEIRO_EXTERNO
  }
  return RAIO_PONTEIRO_EXTERNO
})

const ponteiro = computed(() => {
  const total = ehHora.value ? 12 : 60
  const index = ehHora.value ? valorAtual.value % 12 : valorAtual.value
  return posicaoAngulo(index, total, raioPonteiroValor.value)
})

const ponteiroX = computed(() => ponteiro.value.x)
const ponteiroY = computed(() => ponteiro.value.y)

const valorDoEvento = (e) => {
  const svg = svgRef.value
  if (!svg || typeof svg.getScreenCTM !== 'function') return null
  const pt = svg.createSVGPoint()
  pt.x = e.clientX
  pt.y = e.clientY
  const loc = pt.matrixTransform(svg.getScreenCTM().inverse())
  const dx = loc.x - CX
  const dy = loc.y - CY
  const dist = Math.sqrt(dx * dx + dy * dy)
  let angulo = (Math.atan2(dy, dx) * 180) / Math.PI + 90
  if (angulo < 0) angulo += 360

  if (ehHora.value) {
    const index = Math.round((angulo / 360) * 12) % 12
    return dist < LIMIAR_ANEL ? index + 12 : index
  }
  return Math.round((angulo / 360) * 60) % 60
}

const aplicarValor = (valor) => {
  if (valor === null || valor === undefined) return
  valorAtual.value = valor
  emit('update:modelValue', valor)
}

const onPointerDown = (e) => {
  dragging.value = true
  try {
    svgRef.value?.setPointerCapture?.(e.pointerId)
  } catch { /* ignora */ }
  aplicarValor(valorDoEvento(e))
}

const onPointerMove = (e) => {
  if (!dragging.value) return
  aplicarValor(valorDoEvento(e))
}

const onPointerUp = (e) => {
  if (!dragging.value) return
  dragging.value = false
  try {
    svgRef.value?.releasePointerCapture?.(e.pointerId)
  } catch { /* ignora */ }
  emit('selecionar', valorAtual.value)
}
</script>

<style scoped>
.relogio-circular {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.relogio-svg {
  width: 100%;
  max-width: 300px;
  height: auto;
  touch-action: none;
  user-select: none;
  cursor: pointer;
}

.relogio-anel {
  fill: var(--bs-tertiary-bg);
  stroke: var(--bs-border-color);
  stroke-width: 1;
}

.relogio-disco {
  fill: var(--bs-body-bg);
  stroke: var(--bs-border-color-translucent);
  stroke-width: 1;
}

.relogio-ponteiro {
  stroke: var(--bs-info);
  stroke-width: 3;
  stroke-linecap: round;
}

.relogio-eixo {
  fill: var(--bs-info);
}

.relogio-ponta {
  fill: var(--bs-info);
  stroke: var(--bs-body-bg);
  stroke-width: 2;
}

.relogio-rotulo {
  fill: var(--bs-body-color);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: fill 0.15s ease;
}

.relogio-rotulo-ativo {
  fill: var(--bs-info);
  font-weight: 700;
}

.relogio-smartphone .relogio-rotulo {
  font-size: 13px;
}
</style>
